'use client';

import {
  Button,
  HeadingItem,
  IconButton,
  MetaDefault,
  Spinner,
  Tooltip,
  toasterApi,
} from '@terra/ui-design-system';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Bug02Icon, WorkHistoryIcon, Cancel01Icon } from '@terra/ui-icons';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  type CrawlerSession,
  type HistoryFilters,
  isActiveSession,
  useCrawlerHistory,
  useCrawlerSessions,
} from '../../hooks/useCrawlerSessions';
import { useApplicationCredentialsList } from '../../hooks/api/useApplicationCredentialsList';
import { useCrawlerLastFinishedByCredential } from '../../hooks/api/useCrawlerLastFinishedByCredential';
import { getAuthHeaders } from '../../utils/authHeaders';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { useProjectStore } from '../../store/projectStore';
import { useCrawlerUnseenStore } from '../../store/crawlerUnseenStore';
import { CrawlerCredentialRow } from './CrawlerCredentialRow';
import {
  formatElapsedTimer,
  latestSessionMetaByCredential,
  mergeLastSessionMetaByCredential,
  oldestCreatedAt,
} from './crawlerHistory.utils';

const EMPTY_HISTORY_FILTERS: HistoryFilters = {};

type CrawlerMonitorPanelProps = {
  applicationId: string;
  onOpenHistory: () => void;
  onClose?: () => void;
};

export function CrawlerMonitorPanel({
  applicationId,
  onOpenHistory,
  onClose,
}: CrawlerMonitorPanelProps) {
  const t = useTranslations('crawler.crawlerMonitorPanel');
  const tCommon = useTranslations('common');
  const selectedCompany = useProjectStore((state) => state.selectedCompany);
  const tenantId = selectedCompany?.tenantId;
  const hasHistoryUnseen = useCrawlerUnseenStore((state) =>
    state.hasHistoryUnseen(applicationId),
  );
  const [triggeringCredentials, setTriggeringCredentials] = useState<
    Set<string>
  >(new Set());
  const [triggeringAll, setTriggeringAll] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const { data: credentialsData } = useApplicationCredentialsList({
    applicationId,
  });
  const credentials = useMemo(
    () => credentialsData?.credentials ?? [],
    [credentialsData?.credentials],
  );
  const {
    sessions,
    sessionsByCredential,
    lastFinishedMetaByCredential,
    isLoading,
    abortByCredential,
    abortAll,
    refetch,
    abortingCredentialIds,
    isAbortingAll,
  } = useCrawlerSessions({
    applicationId,
    enabled: true,
  });
  const hasActiveAutomatedSessions = useMemo(
    () =>
      sessions.some(
        (session) =>
          isActiveSession(session) && session.crawlerType !== 'MANUAL',
      ),
    [sessions],
  );
  const stoppingAll = isAbortingAll && hasActiveAutomatedSessions;
  const { sessions: historySessions } = useCrawlerHistory({
    applicationId,
    filters: EMPTY_HISTORY_FILTERS,
    enabled: true,
  });
  const {
    lastFinishedByCredential: lastFinishedFromApi,
    isLoading: isLastFinishedLoading,
  } = useCrawlerLastFinishedByCredential({
    applicationId,
    enabled: true,
  });
  const lastSessionMetaByCredential = useMemo(
    () =>
      mergeLastSessionMetaByCredential({
        sources: [
          lastFinishedFromApi,
          latestSessionMetaByCredential({
            sessions: [...historySessions, ...sessions],
          }),
          lastFinishedMetaByCredential,
        ],
      }),
    [
      historySessions,
      sessions,
      lastFinishedFromApi,
      lastFinishedMetaByCredential,
    ],
  );
  const credentialGroups = useMemo(() => {
    const groups: Array<{
      credentialId: string | null;
      credentialName: string;
      credentialRole?: string;
      isAnonymous?: boolean;
      isDisabled?: boolean;
      sessions: CrawlerSession[];
    }> = [];

    credentials.forEach((credential) => {
      groups.push({
        credentialId: credential.id,
        credentialName: credential.username || credential.id,
        credentialRole: credential.role,
        isDisabled: credential.isDisabled,
        sessions: sessionsByCredential[credential.id] || [],
      });
    });
    groups.push({
      credentialId: null,
      credentialName: t('anonymous'),
      isAnonymous: true,
      sessions: sessionsByCredential['anonymous'] || [],
    });

    return groups;
  }, [credentials, sessionsByCredential, t]);
  const activeNonManualSessions = useMemo(
    () =>
      sessions.filter(
        (session) =>
          isActiveSession(session) && session.crawlerType !== 'MANUAL',
      ),
    [sessions],
  );
  const aggregateStartedAt = useMemo(
    () =>
      oldestCreatedAt(
        activeNonManualSessions.map((session) => session.createdAt),
      ) ?? null,
    [activeNonManualSessions],
  );

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);
  const handleTriggerCrawler = async (credentialId: string | null) => {
    if (!applicationId || !tenantId) return;
    const trackingId = credentialId || 'anonymous';

    try {
      setTriggeringCredentials((prev) => new Set(prev).add(trackingId));
      const authHeaders = await getAuthHeaders();
      const response = await applicationsClient.runCrawler({
        params: { id: applicationId },
        body: {
          credentialId,
        },
        overrideClientOptions: {
          baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
        },
        extraHeaders: {
          ...authHeaders,
        },
      });

      if (response.status !== 200) {
        throw new Error(
          `Failed to trigger crawler (status: ${response.status})`,
        );
      }

      const maxAttempts = 30;
      let attempts = 0;
      const pollForSession = async (): Promise<void> => {
        const result = await refetch();
        const newSessions = result.data?.sessions || [];
        const hasRunningSession = newSessions.some(
          (s: CrawlerSession) =>
            isActiveSession(s) &&
            (credentialId ? s.credentialId === credentialId : !s.credentialId),
        );

        if (hasRunningSession) {
          setTriggeringCredentials((prev) => {
            const next = new Set(prev);

            next.delete(trackingId);

            return next;
          });

          return;
        }

        if (attempts >= maxAttempts) {
          setTriggeringCredentials((prev) => {
            const next = new Set(prev);

            next.delete(trackingId);

            return next;
          });
          toasterApi.error({
            title: t('startFailedTitle'),
            description: t('startFailedNoSessionDescription'),
          });

          return;
        }

        attempts++;
        await new Promise((resolve) => setTimeout(resolve, 1000));

        return pollForSession();
      };

      await pollForSession();
    } catch (error: unknown) {
      setTriggeringCredentials((prev) => {
        const next = new Set(prev);

        next.delete(trackingId);

        return next;
      });
      toasterApi.error({
        title: t('startFailedTitle'),
        description:
          error instanceof Error && error.message
            ? error.message
            : t('startFailedDescription'),
      });
    }
  };

  const handleTriggerAll = async () => {
    if (!applicationId || !tenantId) return;

    try {
      setTriggeringAll(true);
      const enabledGroups = credentialGroups.filter(
        (g) => !g.isDisabled && !g.sessions.some(isActiveSession),
      );

      await Promise.allSettled(
        enabledGroups.map((group) => handleTriggerCrawler(group.credentialId)),
      );
      await refetch();
    } finally {
      setTriggeringAll(false);
    }
  };

  const handleStopAll = async () => {
    if (isAbortingAll) {
      return;
    }

    try {
      await abortAll();
    } catch (error: unknown) {
      toasterApi.error({
        title: t('stopFailedTitle'),
        description:
          error instanceof Error && error.message
            ? error.message
            : t('stopFailedDescription'),
      });
    }
  };

  if (isLoading || isLastFinishedLoading) {
    return (
      <Box p="4" display="flex" justifyContent="center">
        <Spinner size="sm" />
      </Box>
    );
  }

  const historyLabel = t('history');
  const closeLabel = tCommon('close');
  const showStopAll = activeNonManualSessions.length > 0;

  return (
    <VStack gap="0" w="full" alignItems="stretch">
      <HStack
        justifyContent="space-between"
        w="full"
        px="3"
        py="3"
        gap="2"
        borderBottomWidth="1px"
        borderColor="border.muted"
      >
        <HStack gap="2" alignItems="center">
          <Bug02Icon size={18} />
          <HeadingItem>{t('title')}</HeadingItem>
        </HStack>

        <HStack gap="2">
          {showStopAll ? (
            <Button
              size="xs"
              variant="outline"
              disabled={stoppingAll}
              onClick={handleStopAll}
            >
              {stoppingAll ? <Spinner size="xs" /> : t('stopAll')}
            </Button>
          ) : (
            <Button
              size="xs"
              variant="outline"
              disabled={triggeringAll}
              onClick={handleTriggerAll}
            >
              {triggeringAll ? <Spinner size="xs" /> : t('runAll')}
            </Button>
          )}
          <Tooltip title={historyLabel} positioning={{ strategy: 'fixed' }}>
            <Box position="relative">
              <IconButton
                variant="ghost"
                size="xs"
                onClick={onOpenHistory}
                aria-label={t('historyAriaLabel')}
              >
                <WorkHistoryIcon size={16} />
              </IconButton>
              {hasHistoryUnseen ? (
                <Box
                  position="absolute"
                  top="1px"
                  right="1px"
                  w="2"
                  h="2"
                  borderRadius="full"
                  bg="bg.status.resolved"
                />
              ) : null}
            </Box>
          </Tooltip>
          {onClose && (
            <Tooltip title={closeLabel} positioning={{ strategy: 'fixed' }}>
              <IconButton
                variant="ghost"
                size="xs"
                onClick={onClose}
                aria-label={t('closeAriaLabel')}
              >
                <Cancel01Icon size={16} />
              </IconButton>
            </Tooltip>
          )}
        </HStack>
      </HStack>

      {activeNonManualSessions.length > 0 && aggregateStartedAt ? (
        <HStack
          w="full"
          px="3"
          py="2"
          gap="2"
          justifyContent="space-between"
          bg="bg.subtle"
          borderBottomWidth="1px"
          borderColor="border.muted"
        >
          <MetaDefault>
            {t('crawlingCount', { count: activeNonManualSessions.length })}
          </MetaDefault>
          <MetaDefault color="fg.muted">
            {formatElapsedTimer({
              startedAt: aggregateStartedAt,
              now: new Date(nowMs),
            })}
          </MetaDefault>
        </HStack>
      ) : null}

      <VStack gap="0" w="full" alignItems="stretch">
        {credentialGroups.map((group, index) => {
          const key = group.credentialId || 'anonymous';
          const lastMeta = lastSessionMetaByCredential[key];
          const isLast = index === credentialGroups.length - 1;

          return (
            <Fragment key={key}>
              <CrawlerCredentialRow
                credentialId={group.credentialId}
                credentialName={group.credentialName}
                credentialRole={group.credentialRole}
                isAnonymous={group.isAnonymous}
                isDisabled={group.isDisabled}
                sessions={group.sessions}
                lastFinishedAt={lastMeta?.finishedAt}
                lastCrawlerType={lastMeta?.crawlerType}
                nowMs={nowMs}
                hideDivider={isLast}
                onTriggerCrawler={() =>
                  handleTriggerCrawler(group.credentialId)
                }
                onAbortByCredential={abortByCredential}
                isTriggering={triggeringCredentials.has(key)}
                abortingCredentialIds={abortingCredentialIds}
                isAbortingAll={isAbortingAll}
              />
            </Fragment>
          );
        })}
      </VStack>
    </VStack>
  );
}
